import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    Button,
    Alert,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CircularProgress,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuth } from '../components/AuthContext';
import { API_BASE_URL } from '../components/api.js';

// ─── helpers ────────────────────────────────────────────────────────────────

const toLocalDate = (value) =>
    value ? new Date(value.getFullYear(), value.getMonth(), value.getDate()) : null;

const formatDate = (date) =>
    date
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
        : null;

const formatDisplay = (dateStr) =>
    dateStr ? dateStr.split('-').reverse().join('/') : '';

const formatValue = (value, formatType) => {
    if (value === null || value === undefined) return 'N/A';
    const f = parseFloat(value);
    if (isNaN(f)) return 'N/A';
    switch (formatType) {
        case 'R$':
            return `R$ ${f.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
        case 'Ratio':
            return f.toFixed(2).replace('.', ',') + '%';
        case 'Int':
            return Math.round(f).toLocaleString('pt-BR');
        default:
            return f.toLocaleString('pt-BR');
    }
};

const COMPARISON_PERIODS = ['Ano', 'Ano-1', 'Ano-2', 'Ano-3'];

// ─── component ──────────────────────────────────────────────────────────────

function DashboardPeriodo() {
    const { authenticatedFetch, logout } = useAuth();

    const [mode, setMode] = useState('year');
    const [dateRef, setDateRef] = useState(new Date());
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [lastClosedDate, setLastClosedDate] = useState(null);

    const [resultado, setResultado] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Busca a última data fechada ao montar
    useEffect(() => {
        const fetchLastDate = async () => {
            try {
                const response = await authenticatedFetch(
                    `${API_BASE_URL}/api/dashboard-periodo-ultima-data-fechada/`
                );
                if (!response.ok) {
                    if (response.status === 401) logout();
                    throw new Error('Erro ao buscar última data fechada');
                }
                const data = await response.json();
                const [y, m, d] = data.last_closed_date.split('-');
                setLastClosedDate(new Date(Number(y), Number(m) - 1, Number(d)));
            } catch (err) {
                setError('Não foi possível identificar a última data fechada.');
            }
        };
        fetchLastDate();
    }, [authenticatedFetch, logout]);

    const handleModeChange = (_, newMode) => {
        if (newMode) setMode(newMode);
        setResultado(null);
        setError(null);
    };

    const handleBuscar = async () => {
        if (!lastClosedDate) {
            setError('Sistema ainda não identificou a última data válida.');
            return;
        }

        setError(null);
        setResultado(null);

        const payload = { mode, last_closed_date: formatDate(lastClosedDate) };

        if (mode === 'period') {
            if (!startDate || !endDate) {
                setError('Selecione data inicial e final.');
                return;
            }
            payload.start_date = formatDate(startDate);
            payload.end_date = formatDate(endDate);
        } else {
            payload.reference_date = formatDate(dateRef);
        }

        setLoading(true);
        try {
            const response = await authenticatedFetch(`${API_BASE_URL}/api/dashboard-periodo/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                if (response.status === 401) logout();
                const err = await response.json().catch(() => ({}));
                throw new Error(err.error || `Erro ${response.status}`);
            }
            const data = await response.json();
            setResultado(data);
        } catch (err) {
            setError(`Falha ao carregar dados: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // ─── render tabela ──────────────────────────────────────────────────────

    const renderTable = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
                    <CircularProgress size={24} />
                    <Typography>Carregando dados...</Typography>
                </Box>
            );
        }

        if (!resultado) return null;

        const { metrics, period_start, period_end } = resultado;

        const compHeaders = metrics[0]?.comparisons ?? [];

        return (
            <TableContainer component={Paper} elevation={0} sx={{ mt: 2, width: 'fit-content' }}>
                <Table size="small" sx={{ tableLayout: 'fixed', width: 'auto' }}>
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        {/* Linha 1: labels das colunas */}
                        <TableRow>
                            <TableCell rowSpan={2} sx={{ fontWeight: 'bold', verticalAlign: 'middle', width: 180 }}>
                                Métrica
                            </TableCell>
                            <TableCell
                                align="center"
                                rowSpan={2}
                                sx={{ fontWeight: 'bold', borderLeft: '1px solid #ddd', verticalAlign: 'middle', width: 160 }}
                            >
                                Período Base
                            </TableCell>
                            {COMPARISON_PERIODS.map((label) => (
                                <TableCell
                                    key={label}
                                    align="center"
                                    sx={{ fontWeight: 'bold', borderLeft: '1px solid #ddd', width: 160 }}
                                >
                                    {label}
                                </TableCell>
                            ))}
                        </TableRow>

                        {/* Linha 2: datas de cada coluna de comparação */}
                        <TableRow>
                            {compHeaders.map((comp) => (
                                <TableCell
                                    key={comp.period}
                                    align="center"
                                    sx={{ borderLeft: '1px solid #ddd', fontSize: '0.75rem', color: 'text.secondary' }}
                                >
                                    {formatDisplay(comp.date_start)}
                                    {comp.date_start !== comp.date_end && (
                                        <> → {formatDisplay(comp.date_end)}</>
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>

                        {/* Linha 3: data do período base */}
                        <TableRow>
                            <TableCell
                                colSpan={1}
                                sx={{ fontSize: '0.75rem', color: 'text.secondary', py: 0.5 }}
                            />
                            <TableCell
                                align="center"
                                sx={{
                                    borderLeft: '1px solid #ddd',
                                    fontSize: '0.75rem',
                                    color: 'primary.main',
                                    fontWeight: 'bold',
                                    py: 0.5,
                                }}
                            >
                                {formatDisplay(period_start)}
                                {period_start !== period_end && <> → {formatDisplay(period_end)}</>}
                            </TableCell>
                            {COMPARISON_PERIODS.map((label) => (
                                <TableCell key={label} sx={{ borderLeft: '1px solid #ddd', py: 0.5 }} />
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {metrics.map((metric) => (
                            <TableRow key={metric.key} hover>
                                <TableCell sx={{ fontWeight: 'bold' }}>{metric.name}</TableCell>

                                {/* Coluna base — só valor, sem seta */}
                                <TableCell align="right" sx={{ borderLeft: '1px solid #ddd' }}>
                                    <Typography variant="body2" fontWeight="bold">
                                        {formatValue(metric.base_value, metric.format)}
                                    </Typography>
                                </TableCell>

                                {/* Colunas Ano / Ano-1 / Ano-2 — valor + seta de variação */}
                                {metric.comparisons.map((c) => {
                                    const isPositive = c.diff_perc >= 0;
                                    const Icon = isPositive ? ArrowUpwardIcon : ArrowDownwardIcon;
                                    const color = isPositive ? 'success.dark' : 'error.main';

                                    return (
                                        <TableCell key={c.period} align="center" sx={{ borderLeft: '1px solid #eee' }}>
                                            <Typography variant="body2" fontWeight={600}>
                                                {formatValue(c.value, metric.format)}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color, mt: 0.25 }}>
                                                <Icon sx={{ fontSize: 13, mr: 0.5 }} />
                                                <Typography variant="caption" fontWeight="bold">
                                                    {c.diff_perc.toFixed(2).replace('.', ',')}%
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    // ─── render principal ───────────────────────────────────────────────────

    return (
        <Box sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h5" fontWeight="bold">
                Dashboard Comparativo por Período
            </Typography>

            {lastClosedDate && (
                <Alert severity="info">
                    Última data fechada: {lastClosedDate.toLocaleDateString('pt-BR')}
                </Alert>
            )}
            {error && <Alert severity="error">{error}</Alert>}

            <Paper elevation={3} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3, width: 'fit-content', minWidth: 200 }}>
                <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange}>
                    <ToggleButton value="year">Ano</ToggleButton>
                    <ToggleButton value="month">Mês</ToggleButton>
                    <ToggleButton value="period">Período</ToggleButton>
                </ToggleButtonGroup>

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    {mode === 'period' ? (
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <DatePicker
                                label="Data Inicial"
                                value={startDate}
                                onChange={(v) => setStartDate(toLocalDate(v))}
                                format="dd/MM/yyyy"
                            />
                            <DatePicker
                                label="Data Final"
                                value={endDate}
                                onChange={(v) => setEndDate(toLocalDate(v))}
                                format="dd/MM/yyyy"
                            />
                        </Box>
                    ) : (
                        <DatePicker
                            label="Data de Referência"
                            value={dateRef}
                            onChange={(v) => setDateRef(toLocalDate(v))}
                            format="dd/MM/yyyy"
                        />
                    )}
                </LocalizationProvider>

                <Button variant="contained" onClick={handleBuscar} disabled={loading} sx={{ alignSelf: 'flex-start' }}>
                    {loading ? 'Buscando...' : 'Buscar'}
                </Button>
            </Paper>

            {resultado && (
                <Paper elevation={3} sx={{ p: 2, overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
                    {renderTable()}
                </Paper>
            )}
        </Box>
    );
}

export default DashboardPeriodo;